// PR-DOCVAULT JUN03 — Pack Builder.
//
// Pick vehicles (rows) × document types (columns), preview shows
// presence/missing per vehicle, Generate downloads either a single
// merged PDF or a packs.zip.
import { useEffect, useMemo, useState, useRef } from 'react';
import { FileBadge, Search, Loader2, AlertCircle, Download, ChevronsUpDown, FolderUp, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api';
import { getStoredToken } from '@/lib/api';
import { EntityDocumentsPanel, VEHICLE_SLOTS, COMPANY_SLOTS, DRIVER_SLOTS } from '@/components/documents/EntityDocumentsPanel';

interface Vehicle {
  id: string;
  plateEn: string;
  driverId: string | null;
  driverName: string | null;
}
interface PreviewRow {
  vehicleId: string;
  plate: string;
  driver: { id: string; name: string | null } | null;
  present: string[];
  missing: string[];
}

const COLS: { slug: string; label: string; isDriver?: boolean }[] = [
  { slug: 'istimara',            label: 'Istimara' },
  { slug: 'cr',                  label: 'CR' },
  { slug: 'operation_card',      label: 'Op. card' },
  { slug: 'insurance',           label: 'Insurance' },
  { slug: 'periodic_inspection', label: 'Inspection' },
  { slug: 'iqama',               label: 'Iqama',          isDriver: true },
  { slug: 'driving_license',     label: 'Ruksa',          isDriver: true },
];

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

export default function PackBuilderPage() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectedCols, setSelectedCols] = useState<Set<string>>(
    new Set(['istimara', 'cr', 'operation_card', 'insurance', 'periodic_inspection']),
  );
  const [preview, setPreview] = useState<PreviewRow[] | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Shadow preview — runs once across ALL vehicles × ALL doc types so
  // the operator sees per-row progress badges (n/m) and per-cell dots
  // independent of what they have selected. Re-fetched when a modal
  // closes (uploads invalidate it).
  const [matrix, setMatrix] = useState<Map<string, { present: string[]; missing: string[]; driver: string | null }>>(new Map());
  const allDocSlugs = ['istimara', 'cr', 'operation_card', 'insurance', 'periodic_inspection', 'iqama', 'driving_license'];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get('/api/v1/vehicles', { params: { pageSize: 500, status: 'active' } })
      .then(({ data }) => {
        if (cancelled) return;
        const rows: Vehicle[] = (data.items ?? []).map((v: { id: string; plateEn: string; currentDriverId?: string | null; currentDriverName?: string | null; driver?: { id?: string; name?: string } }) => ({
          id: v.id,
          plateEn: v.plateEn,
          driverId: v.currentDriverId ?? v.driver?.id ?? null,
          driverName: v.currentDriverName ?? v.driver?.name ?? null,
        }));
        setVehicles(rows);
      })
      .catch(() => setError('Failed to load vehicles'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Status filter: 'all' = no filter, 'missing-any' = at least one slot
  // empty, 'complete' = every slot present, 'missing:<slug>' = missing
  // this specific doc type. Operators with 50+ vehicles need this to
  // bulk-attack uploads ("show me everyone missing insurance").
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = vehicles;
    if (q) out = out.filter(v => v.plateEn.toLowerCase().includes(q));
    if (statusFilter !== 'all' && matrix.size > 0) {
      out = out.filter(v => {
        const m = matrix.get(v.id);
        if (!m) return true;
        if (statusFilter === 'missing-any') return m.missing.length > 0;
        if (statusFilter === 'complete')    return m.missing.length === 0;
        if (statusFilter.startsWith('missing:')) return m.missing.includes(statusFilter.slice(8));
        return true;
      });
    }
    return out;
  }, [vehicles, search, statusFilter, matrix]);

  // Fleet-wide stats from the shadow matrix.
  const fleetStats = useMemo(() => {
    const total = vehicles.length;
    let complete = 0;
    const missingByKind: Record<string, number> = {};
    for (const v of vehicles) {
      const m = matrix.get(v.id);
      if (!m) continue;
      if (m.missing.length === 0) complete++;
      for (const slug of m.missing) missingByKind[slug] = (missingByKind[slug] ?? 0) + 1;
    }
    return { total, complete, missingByKind };
  }, [vehicles, matrix]);

  const includeDriver = COLS.filter(c => c.isDriver).some(c => selectedCols.has(c.slug));

  function toggleRow(id: string) {
    const next = new Set(selectedRows);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedRows(next);
    setPreview(null);
  }
  function toggleAllRows() {
    if (selectedRows.size === filtered.length) setSelectedRows(new Set());
    else setSelectedRows(new Set(filtered.map(v => v.id)));
    setPreview(null);
  }
  function toggleCol(slug: string) {
    const next = new Set(selectedCols);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    setSelectedCols(next);
    setPreview(null);
  }
  function toggleDriverCols() {
    const next = new Set(selectedCols);
    const has = COLS.filter(c => c.isDriver).every(c => next.has(c.slug));
    for (const c of COLS.filter(c => c.isDriver)) {
      if (has) next.delete(c.slug); else next.add(c.slug);
    }
    setSelectedCols(next);
    setPreview(null);
  }

  async function runPreview() {
    setError(null);
    if (selectedRows.size === 0 || selectedCols.size === 0) {
      setPreview(null);
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await apiClient.post('/api/v1/document-packs/preview', {
        vehicleIds: [...selectedRows],
        docTypes:   [...selectedCols],
        includeDriver,
      });
      setPreview(data.items as PreviewRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  }

  // Auto-run preview shortly after the user changes the selection so
  // they always see the present/missing state without needing to click.
  // 350ms debounce keeps it cheap during checkbox-spam.
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    if (selectedRows.size === 0 || selectedCols.size === 0) {
      setPreview(null);
      return;
    }
    previewTimer.current = setTimeout(() => { runPreview(); }, 350);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
    // selectedRows/selectedCols/includeDriver covered by the Sets above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, selectedCols, includeDriver]);

  // True only after a preview ran and confirmed every selected slot is
  // missing across every selected vehicle. The Generate button shows a
  // friendlier message in this state instead of producing a blank PDF.
  const everythingMissing = preview !== null
    && preview.length > 0
    && preview.every(r => r.present.length === 0);

  // For the manage-docs modal: which vehicle (and optionally driver) is
  // the operator editing right now?
  const [docsModalVehicle, setDocsModalVehicle] = useState<Vehicle | null>(null);
  const [docsModalCompany, setDocsModalCompany] = useState(false);

  function refreshAfterDocChange() {
    // Any upload from the modal invalidates the preview cache and the
    // colour dots, so re-run preview AND the shadow matrix.
    if (selectedRows.size > 0 && selectedCols.size > 0) runPreview();
    refreshMatrix();
  }

  // Shadow-matrix fetch: ask the backend for present/missing across
  // every loaded vehicle and every doc type, so we can render the row
  // progress badges without the user having to select anything.
  async function refreshMatrix() {
    if (vehicles.length === 0) return;
    try {
      const { data } = await apiClient.post('/api/v1/document-packs/preview', {
        vehicleIds:    vehicles.map(v => v.id),
        docTypes:      allDocSlugs,
        includeDriver: true,
      });
      const m = new Map<string, { present: string[]; missing: string[]; driver: string | null }>();
      for (const r of (data.items as PreviewRow[])) {
        m.set(r.vehicleId, {
          present: r.present,
          missing: r.missing,
          driver: r.driver?.name ?? null,
        });
      }
      setMatrix(m);
    } catch { /* keep stale matrix on failure */ }
  }
  useEffect(() => {
    if (vehicles.length > 0) refreshMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  async function generate() {
    setError(null);
    if (selectedRows.size === 0 || selectedCols.size === 0) {
      setError('Pick at least one vehicle and one document type.');
      return;
    }
    setGenerating(true);
    try {
      const token = getStoredToken();
      const res = await fetch(API_BASE + '/api/v1/document-packs/build', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          vehicleIds: [...selectedRows],
          docTypes:   [...selectedCols],
          includeDriver,
        }),
      });
      if (!res.ok) {
        // 400 from the new no_documents_present path returns JSON with
        // a friendly message — surface it instead of just the HTTP code.
        let msg = `Build failed (HTTP ${res.status})`;
        try {
          const j = await res.json();
          if (j?.message) msg = j.message;
        } catch { /* not JSON */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') ?? '';
      const m = /filename="?([^";]+)"?/i.exec(cd);
      a.download = m?.[1] ?? (selectedRows.size === 1 ? 'pack.pdf' : 'packs.zip');
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generate failed');
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <header className="flex items-center gap-3">
        <div className="rounded-lg bg-emerald-500/10 p-2 text-emerald-600">
          <FileBadge className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-semibold">Pack builder</h1>
          <p className="text-sm text-muted-foreground">
            Pick vehicles and document types — Generate downloads a clean merged PDF
            per vehicle (zipped if more than one).
          </p>
        </div>
      </header>

      <div className="flex items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plate"
            className="pl-7 pr-3 py-1.5 text-sm rounded border border-border bg-background"
          />
        </div>
        <Button size="sm" variant="outline" onClick={toggleDriverCols} className="gap-1.5">
          <ChevronsUpDown className="h-3.5 w-3.5" /> Toggle driver docs
        </Button>
        <Button size="sm" variant="outline" onClick={() => setDocsModalCompany(true)} className="gap-1.5">
          <FolderUp className="h-3.5 w-3.5" /> Company docs
        </Button>
        <div className="flex-1" />
        {previewing && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        <Button
          size="sm"
          onClick={generate}
          disabled={generating || everythingMissing || selectedRows.size === 0 || selectedCols.size === 0}
          className="gap-1.5"
          title={everythingMissing ? 'Upload at least one selected document type first' : ''}
        >
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Generate
        </Button>
      </div>

      {/* Fleet-wide summary + status filters — helps the operator see
          at a glance where the holes are and jump straight to them. */}
      <div className="rounded border border-border bg-card p-3 space-y-2 text-sm">
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1">
          <div>
            <span className="font-semibold">{fleetStats.complete}</span>
            <span className="text-muted-foreground"> of {fleetStats.total} vehicles fully ready</span>
          </div>
          {Object.entries(fleetStats.missingByKind)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([slug, n]) => {
              const label = COLS.find(c => c.slug === slug)?.label ?? slug;
              return (
                <button
                  key={slug}
                  onClick={() => setStatusFilter(statusFilter === 'missing:' + slug ? 'all' : 'missing:' + slug)}
                  className={
                    'text-xs px-2 py-0.5 rounded-full border ' +
                    (statusFilter === 'missing:' + slug
                      ? 'bg-rose-500/15 border-rose-500/50 text-rose-700'
                      : 'bg-rose-500/5 border-rose-500/20 text-rose-700 hover:bg-rose-500/10')
                  }
                  title={'Show only vehicles missing ' + label}
                >
                  {n} missing {label}
                </button>
              );
            })}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Filter:</span>
          {[
            { key: 'all',          label: 'All' },
            { key: 'missing-any',  label: 'Missing any' },
            { key: 'complete',     label: 'Complete only' },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => setStatusFilter(opt.key)}
              className={
                'px-2 py-0.5 rounded border ' +
                (statusFilter === opt.key
                  ? 'bg-foreground/10 border-foreground/30 text-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground')
              }
            >
              {opt.label}
            </button>
          ))}
          {statusFilter !== 'all' && (
            <button onClick={() => setStatusFilter('all')} className="text-muted-foreground hover:text-foreground underline">
              clear
            </button>
          )}
        </div>
      </div>

      {everythingMissing && (
        <div className="flex items-start gap-2 p-3 rounded border border-amber-500/40 bg-amber-500/10 text-sm text-amber-700">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <div className="font-medium">No documents uploaded yet for the selected slots.</div>
            <div className="text-amber-700/80">
              Click the <FolderUp className="h-3 w-3 inline" /> button on any vehicle row below
              to upload its istimara / operation card / insurance / inspection. Use the
              <span className="font-semibold"> Company docs </span> button above for the CR.
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 rounded border border-destructive/30 bg-destructive/10 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {preview && (
        <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-sm space-y-1">
          <div className="font-medium text-amber-700">Pre-flight</div>
          {preview.filter(r => r.missing.length > 0).length === 0 ? (
            <div className="text-emerald-600">Every selected vehicle has every selected document.</div>
          ) : (
            <ul className="list-disc list-inside text-amber-700">
              {preview.filter(r => r.missing.length > 0).map(r => (
                <li key={r.vehicleId}>
                  <span className="font-mono">{r.plate}</span>
                  {!r.driver && includeDriver && <span className="ml-1 italic">(no driver assigned)</span>}
                  {': '}{r.missing.join(', ')}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="rounded border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr>
              <th className="text-left px-3 py-2 w-10">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && selectedRows.size === filtered.length}
                  onChange={toggleAllRows}
                  aria-label="Select all rows"
                />
              </th>
              <th className="text-left px-3 py-2">Plate</th>
              <th className="text-left px-3 py-2">Driver</th>
              {COLS.map(c => (
                <th key={c.slug} className="text-center px-2 py-2 whitespace-nowrap">
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCols.has(c.slug)}
                      onChange={() => toggleCol(c.slug)}
                    />
                    {c.label}
                  </label>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={3 + COLS.length} className="text-center py-6 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading vehicles…
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={3 + COLS.length} className="text-center py-6 text-muted-foreground">No vehicles.</td></tr>
            )}
            {filtered.map(v => {
              const matrixRow = matrix.get(v.id);
              const totalSlots = COLS.length;
              const filledForVehicle = matrixRow ? matrixRow.present.length : 0;
              const allDone = matrixRow && filledForVehicle === totalSlots;
              return (
                <tr key={v.id} className="border-b border-border/40 hover:bg-muted/20">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(v.id)}
                      onChange={() => toggleRow(v.id)}
                      aria-label={'Select ' + v.plateEn}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono">
                    <button
                      type="button"
                      onClick={() => setDocsModalVehicle(v)}
                      className="inline-flex items-center gap-1.5 hover:underline"
                      title="Manage documents for this vehicle"
                    >
                      <FolderUp className="h-3 w-3 text-muted-foreground" />
                      {v.plateEn}
                    </button>
                    {matrixRow && (
                      <span className={
                        'ml-2 text-[10px] px-1.5 py-0.5 rounded-full ' +
                        (allDone ? 'bg-emerald-500/15 text-emerald-700'
                        : filledForVehicle === 0 ? 'bg-rose-500/15 text-rose-700'
                        : 'bg-amber-500/15 text-amber-700')
                      } title={
                        allDone
                          ? 'All documents uploaded'
                          : 'Missing: ' + matrixRow.missing.join(', ')
                      }>
                        {filledForVehicle}/{totalSlots}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{v.driverName ?? <em>no driver</em>}</td>
                  {COLS.map(c => {
                    const present = matrixRow?.present.includes(c.slug);
                    return (
                      <td key={c.slug} className="text-center px-2 py-2">
                        {matrixRow ? (
                          present ? (
                            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" title="uploaded" />
                          ) : (
                            <span className="inline-block h-2 w-2 rounded-full bg-rose-400/70" title="missing" />
                          )
                        ) : (
                          <span className="text-muted-foreground/40">·</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {docsModalVehicle && (
        <DocsModal
          title={`Documents — ${docsModalVehicle.plateEn}`}
          onClose={() => { setDocsModalVehicle(null); refreshAfterDocChange(); }}
        >
          <EntityDocumentsPanel
            scope="vehicle"
            entityId={docsModalVehicle.id}
            slots={VEHICLE_SLOTS}
            title="Vehicle documents"
          />
          {docsModalVehicle.driverId && (
            <div className="mt-6">
              <EntityDocumentsPanel
                scope="driver"
                entityId={docsModalVehicle.driverId}
                slots={DRIVER_SLOTS}
                title={`Driver documents — ${docsModalVehicle.driverName ?? ''}`}
              />
            </div>
          )}
        </DocsModal>
      )}

      {docsModalCompany && (
        <DocsModal
          title="Company documents"
          onClose={() => { setDocsModalCompany(false); refreshAfterDocChange(); }}
        >
          <EntityDocumentsPanel scope="company" slots={COMPANY_SLOTS} />
        </DocsModal>
      )}
    </div>
  );
}

function DocsModal({
  title, children, onClose,
}: { title: string; children: React.ReactNode; onClose: () => void }) {
  // Click-outside / ESC handled by the outer overlay click; the inner
  // panel stops propagation so clicks inside don't dismiss.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-xl w-full max-w-3xl mt-12 mb-12"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-4">{children}</div>
        <footer className="p-3 border-t border-border flex justify-end">
          <Button size="sm" variant="outline" onClick={onClose}>Done</Button>
        </footer>
      </div>
    </div>
  );
}
